/**
 * Feature Flags Configuration
 * Following constitution requirements for Phase 1/2 control
 *
 * This file manages feature flags to control which functionality
 * is available in different phases of development.
 */

import { getEnvironmentConfig } from './environment';

/**
 * Phase 1 Features (Always Enabled)
 * These features are core to the ArbiWin platform and must always be available
 */
export const PHASE_1_FEATURES = {
  // Authentication & User Management
  USER_REGISTRATION: true,
  USER_LOGIN: true,
  PASSWORD_RESET: true,
  JWT_AUTHENTICATION: true,
  ROLE_BASED_ACCESS: true,

  // Subscription Management
  SUBSCRIPTION_STATUS_CHECK: true,
  MANUAL_SUBSCRIPTION_ACTIVATION: true,
  SUBSCRIPTION_GUARDS: true,
  HTTP_402_HANDLING: true,

  // Surebet Core Functionality
  MARKETS_ANALYSIS_DISPLAY: true,
  DYNAMIC_MARGIN_PROCESSING: true,
  BOOKMAKER_FILTERING: true,
  REAL_TIME_DATA_REFRESH: true,
  AUTO_REFRESH_10_MIN: true,

  // Calculator Functionality
  ARBITRAGE_CALCULATOR: true,
  STAKE_DISTRIBUTION: true,
  PROFIT_CALCULATION: true,
  CLIPBOARD_COPY: true,

  // UI/UX Features
  BILINGUAL_SUPPORT: true,
  RESPONSIVE_DESIGN: true,
  DARK_THEME: true,
  ACCESSIBILITY_SUPPORT: true,
  ERROR_HANDLING: true,
  LOADING_STATES: true,

  // Navigation & Routing
  PUBLIC_PAGES: true,
  PROTECTED_ROUTES: true,
  SUBSCRIPTION_ROUTES: true,
  ROUTE_GUARDS: true
};

/**
 * Phase 2 Features (Disabled in Phase 1)
 * These features will be enabled in Phase 2 with OpenAI integration
 */
export const PHASE_2_FEATURES = {
  // AI Analysis Features
  AI_SUREBET_ANALYSIS: false,
  AI_MARGIN_PREDICTION: false,
  AI_RISK_ASSESSMENT: false,
  AI_CONFIDENCE_SCORES: false,

  // AI Recommendations
  PERSONALIZED_RECOMMENDATIONS: false,
  AI_BETTING_INSIGHTS: false,
  HISTORICAL_PERFORMANCE_AI: false,
  PREDICTIVE_ANALYTICS: false,

  // Enhanced Features
  SMART_FILTERING: false,
  AUTO_STAKE_SUGGESTIONS: false,
  PORTFOLIO_OPTIMIZATION: false,
  ADVANCED_ANALYTICS: false,

  // AI Integration Components
  AI_CHAT_SUPPORT: false,
  AI_TUTORIAL_SYSTEM: false,
  AI_MARKET_INSIGHTS: false,
  AI_TREND_ANALYSIS: false
};

/**
 * Development Features (Environment Dependent)
 */
export const DEVELOPMENT_FEATURES = {
  DEBUG_PANEL: process.env.NODE_ENV === 'development',
  MOCK_DATA: process.env.REACT_APP_USE_MOCK_DATA === 'true',
  ERROR_TESTING: process.env.NODE_ENV === 'development',
  STYLE_TESTING: process.env.NODE_ENV === 'development',
  PERFORMANCE_MONITORING: process.env.REACT_APP_PERFORMANCE_MONITORING !== 'false',
  CONSOLE_LOGGING: process.env.NODE_ENV === 'development',
  API_MOCKING: process.env.REACT_APP_MOCK_API === 'true',
  HOT_RELOAD: process.env.NODE_ENV === 'development'
};

/**
 * Experimental Features (Opt-in)
 */
export const EXPERIMENTAL_FEATURES = {
  VIRTUAL_SCROLLING: process.env.REACT_APP_ENABLE_VIRTUAL_SCROLLING === 'true',
  WEB_WORKERS: process.env.REACT_APP_ENABLE_WEB_WORKERS === 'true',
  SERVICE_WORKER: process.env.REACT_APP_ENABLE_SERVICE_WORKER === 'true',
  OFFLINE_MODE: process.env.REACT_APP_ENABLE_OFFLINE === 'true',
  PUSH_NOTIFICATIONS: process.env.REACT_APP_ENABLE_PUSH === 'true'
};

/**
 * Get all feature flags
 * @returns {Object} Complete feature flags object
 */
export function getAllFeatures() {
  return Object.assign({},
    PHASE_1_FEATURES,
    PHASE_2_FEATURES,
    DEVELOPMENT_FEATURES,
    EXPERIMENTAL_FEATURES
  );
}

/**
 * Check if specific feature is enabled
 * @param {string} featureName - Feature name to check
 * @returns {boolean} True if feature is enabled
 */
export function isFeatureEnabled(featureName) {
  const allFeatures = getAllFeatures();
  return allFeatures[featureName] === true;
}

/**
 * Get enabled features by category
 * @param {string} category - Feature category (phase1, phase2, development, experimental)
 * @returns {Array} Array of enabled feature names
 */
export function getEnabledFeatures(category) {
  let features;

  switch (category) {
    case 'phase1':
      features = PHASE_1_FEATURES;
      break;
    case 'phase2':
      features = PHASE_2_FEATURES;
      break;
    case 'development':
      features = DEVELOPMENT_FEATURES;
      break;
    case 'experimental':
      features = EXPERIMENTAL_FEATURES;
      break;
    default:
      features = getAllFeatures();
  }

  return Object.keys(features).filter(function(key) {
    return features[key] === true;
  });
}

/**
 * Feature flag utilities
 */
export const featureUtils = {
  /**
   * Check if Phase 2 is enabled (should always be false in Phase 1)
   * @returns {boolean} False for Phase 1
   */
  isPhase2Enabled: function() {
    // CRITICAL: Always return false for Phase 1
    // This prevents any AI features from being accidentally enabled
    return false;
  },

  /**
   * Check if AI features are allowed
   * @returns {boolean} False for Phase 1
   */
  isAIEnabled: function() {
    // CRITICAL: Always return false for Phase 1
    return false;
  },

  /**
   * Get feature configuration for component
   * @param {string} componentName - Component name
   * @returns {Object} Component-specific feature flags
   */
  getComponentFeatures: function(componentName) {
    const componentFeatures = {
      auth: {
        registration: PHASE_1_FEATURES.USER_REGISTRATION,
        login: PHASE_1_FEATURES.USER_LOGIN,
        passwordReset: PHASE_1_FEATURES.PASSWORD_RESET,
        jwtAuth: PHASE_1_FEATURES.JWT_AUTHENTICATION
      },

      subscription: {
        statusCheck: PHASE_1_FEATURES.SUBSCRIPTION_STATUS_CHECK,
        manualActivation: PHASE_1_FEATURES.MANUAL_SUBSCRIPTION_ACTIVATION,
        guards: PHASE_1_FEATURES.SUBSCRIPTION_GUARDS
      },

      surebets: {
        display: PHASE_1_FEATURES.MARKETS_ANALYSIS_DISPLAY,
        filtering: PHASE_1_FEATURES.BOOKMAKER_FILTERING,
        autoRefresh: PHASE_1_FEATURES.AUTO_REFRESH_10_MIN,
        realTimeData: PHASE_1_FEATURES.REAL_TIME_DATA_REFRESH
      },

      calculator: {
        arbitrageCalc: PHASE_1_FEATURES.ARBITRAGE_CALCULATOR,
        stakeDistribution: PHASE_1_FEATURES.STAKE_DISTRIBUTION,
        profitCalc: PHASE_1_FEATURES.PROFIT_CALCULATION,
        clipboardCopy: PHASE_1_FEATURES.CLIPBOARD_COPY
      },

      ui: {
        bilingual: PHASE_1_FEATURES.BILINGUAL_SUPPORT,
        responsive: PHASE_1_FEATURES.RESPONSIVE_DESIGN,
        darkTheme: PHASE_1_FEATURES.DARK_THEME,
        accessibility: PHASE_1_FEATURES.ACCESSIBILITY_SUPPORT
      }
    };

    return componentFeatures[componentName] || {};
  },

  /**
   * Check if development tools should be shown
   * @returns {boolean} True if development tools enabled
   */
  showDevelopmentTools: function() {
    return DEVELOPMENT_FEATURES.DEBUG_PANEL || DEVELOPMENT_FEATURES.STYLE_TESTING;
  },

  /**
   * Get performance monitoring features
   * @returns {Object} Performance monitoring configuration
   */
  getPerformanceFeatures: function() {
    return {
      webVitals: DEVELOPMENT_FEATURES.PERFORMANCE_MONITORING,
      consoleLogging: DEVELOPMENT_FEATURES.CONSOLE_LOGGING,
      errorTracking: PHASE_1_FEATURES.ERROR_HANDLING
    };
  }
};

/**
 * Feature validation
 */
export function validateFeatureFlags() {
  const errors = [];
  const warnings = [];

  // Ensure Phase 2 features are disabled in Phase 1
  const phase2Features = Object.keys(PHASE_2_FEATURES);
  phase2Features.forEach(function(feature) {
    if (PHASE_2_FEATURES[feature] === true) {
      errors.push('Phase 2 feature "' + feature + '" must be disabled in Phase 1');
    }
  });

  // Check for AI feature leakage
  if (featureUtils.isAIEnabled()) {
    errors.push('AI features detected - this violates Phase 1 constraints');
  }

  // Validate environment consistency
  const envConfig = getEnvironmentConfig();
  if (envConfig.nodeEnv === 'production' && DEVELOPMENT_FEATURES.DEBUG_PANEL) {
    warnings.push('Debug panel is enabled in production environment');
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
    warnings: warnings
  };
}

/**
 * Initialize feature flags with validation
 * @returns {Object} Validated feature configuration
 */
export function initializeFeatures() {
  const validation = validateFeatureFlags();

  if (!validation.isValid) {
    console.error('Feature flag validation errors:', validation.errors);
    throw new Error('Invalid feature configuration: ' + validation.errors.join(', '));
  }

  if (validation.warnings.length > 0) {
    console.warn('Feature flag warnings:', validation.warnings);
  }

  const envConfig = getEnvironmentConfig();
  if (envConfig.debug.enabled) {
    console.log('Feature flags initialized:', {
      phase1Features: getEnabledFeatures('phase1').length,
      phase2Features: getEnabledFeatures('phase2').length,
      developmentFeatures: getEnabledFeatures('development').length,
      experimentalFeatures: getEnabledFeatures('experimental').length
    });
  }

  return getAllFeatures();
}

export default {
  PHASE_1_FEATURES,
  PHASE_2_FEATURES,
  DEVELOPMENT_FEATURES,
  EXPERIMENTAL_FEATURES,
  getAllFeatures,
  isFeatureEnabled,
  getEnabledFeatures,
  featureUtils,
  validateFeatureFlags,
  initializeFeatures
};
